import { cva } from "class-variance-authority";

export {
  formErrorMessageCva as errorMessageCva,
  formLabelCva as labelCva,
  formRequiredCva as requiredCva,
} from "../Form/form.cva";

// Match VInput formSubWrapperCva: flex + full width so the control tracks its grid cell.
export const containerCva = cva("font-roboto text-(--text)", {
  variants: {
    layout: {
      "is-column": "flex w-full flex-col",
      "is-row": "flex w-full flex-row items-center gap-3",
    },
  },
  defaultVariants: {
    layout: "is-column",
  },
});

// The pill matches VInput's inputCva: same border, background, heights and error border.
export const inputContainerCva = cva(
  "relative flex w-full items-center rounded-full border border-(--input-border) bg-(--input-background) transition-[border-color,box-shadow] duration-150",
  {
    variants: {
      size: {
        small: "h-7",
        medium: "h-8",
        large: "h-10",
      },
      // The stepper buttons sit 4px from the edge of the pill.
      stepper: {
        true: "gap-0.5 px-1",
        false: null,
      },
      disabled: {
        true: "cursor-not-allowed opacity-50",
        false: null,
      },
      invalid: {
        true: "border-danger-600 has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-danger-600/25",
        false: "has-[input:focus-visible]:border-primary-500 has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-primary-500/25",
      },
    },
    defaultVariants: {
      size: "medium",
      stepper: true,
      disabled: false,
      invalid: false,
    },
  },
);

export const stepButtonCva = cva(
  "flex shrink-0 select-none items-center justify-center rounded-full bg-primary-50 text-primary-800 transition-colors duration-150 hover:bg-primary-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-40 dark:bg-dark-700 dark:text-primary-400 dark:hover:bg-dark-600",
  {
    variants: {
      size: {
        small: "size-5",
        medium: "size-6",
        large: "size-8",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    defaultVariants: {
      size: "medium",
      disabled: false,
    },
  },
);

// Same look as VInput's clear icon, as a small round button so it has a focus ring of its own.
export const clearButtonCva = cva(
  "flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-xs text-gray-800 transition-colors duration-150 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-200 dark:hover:bg-dark-700",
  {
    variants: {
      stepper: {
        true: null,
        false: "mr-2",
      },
    },
    defaultVariants: {
      stepper: true,
    },
  },
);

export const inputCva = cva(
  "h-full w-12 min-w-0 flex-1 border-none bg-transparent text-(--input-text) outline-hidden placeholder:text-(--input-placeholder) [appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none",
  {
    variants: {
      size: {
        small: "text-base sm:text-xs",
        medium: "text-base sm:text-sm",
        large: "text-base sm:text-sm",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: null,
      },
      /**
       * Where the number sits. `center` between the stepper buttons; `end` against a unit that follows it, so
       * the pair reads "3 nights" in the middle; `start` after a prefix in the middle; `right` without a stepper.
       */
      align: {
        center: "text-center",
        end: "text-right",
        start: "text-left",
        right: "text-right",
      },
      // Without a stepper the number keeps VInput's padding on the sides nothing else covers.
      padStart: {
        true: "pl-5",
        false: null,
      },
      padEnd: {
        true: "pr-5",
        false: null,
      },
    },
    defaultVariants: {
      size: "medium",
      disabled: false,
      align: "center",
      padStart: false,
      padEnd: false,
    },
  },
);

export const affixCva = cva("min-w-0 select-none truncate text-gray-600 dark:text-gray-400", {
  variants: {
    size: {
      small: "text-base sm:text-xs",
      medium: "text-base sm:text-sm",
      large: "text-base sm:text-sm",
    },
    /**
     * `prefix` and `unit` share the free space with the number between the stepper buttons, so the group stays
     * centered with a 4px gap. Without a stepper the prefix sits at the left edge and the unit follows the number.
     */
    placement: {
      "prefix-stepper": "flex-1 pr-1 text-right",
      "unit-stepper": "flex-1 pl-1 text-left",
      "prefix": "shrink-0 pl-5",
      "unit": "shrink-0 pl-1 pr-5",
    },
  },
  defaultVariants: {
    size: "medium",
    placement: "unit",
  },
});
